import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Truck, Pencil, Trash2, Star, ShieldAlert, RotateCcw, Filter, Award, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';
import CreateSupplierModal from '../../../components/admin/procurement/CreateSupplierModal';
import EditSupplierModal from '../../../components/admin/procurement/EditSupplierModal';
import SupplierDocuments from '../../../components/admin/procurement/SupplierDocuments';
import PromoteCounterpartyModal from '../../../components/admin/PromoteCounterpartyModal';
import AlsoBadge from '../../../components/admin/AlsoBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Suppliers = () => {
  const navigate = useNavigate();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [promoteSource, setPromoteSource] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, blocked: 0 });
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchSuppliers();
    fetchStats();
  }, [pagination, searchTerm, statusFilter, categoryFilter]);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('skip', pagination.skip);
      params.append('limit', pagination.limit);
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      if (categoryFilter) params.append('category', categoryFilter);

      const response = await fetch(
        `${API_URL}/api/procurement/suppliers?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch suppliers');

      const data = await response.json();
      setSuppliers(data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load suppliers',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/procurement/suppliers/stats`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch stats');

      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;

    try {
      const response = await fetch(`${API_URL}/api/procurement/suppliers/${supplierId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete supplier');

      toast({
        title: 'Success',
        description: 'Supplier deleted successfully'
      });
      fetchSuppliers();
      fetchStats();
    } catch (error) {
      console.error('Error deleting supplier:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete supplier',
        variant: 'destructive'
      });
    }
  };

  const handleRestore = async (supplierId) => {
    try {
      const response = await fetch(`${API_URL}/api/procurement/suppliers/${supplierId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to restore supplier');

      toast({
        title: 'Success',
        description: 'Supplier restored successfully'
      });
      fetchSuppliers();
      fetchStats();
    } catch (error) {
      console.error('Error restoring supplier:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore supplier',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (supplier) => {
    setSelectedSupplier(supplier);
    setIsEditModalOpen(true);
  };

  const handleSuccessCreate = () => {
    fetchSuppliers();
    fetchStats();
    setIsCreateModalOpen(false);
  };

  const handleSuccessEdit = () => {
    fetchSuppliers();
    setIsEditModalOpen(false);
  };

  const handleNextPage = () => {
    setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
  };

  const handlePreviousPage = () => {
    setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', className: 'bg-green-100 text-green-800' },
      inactive: { label: 'Inactive', className: 'bg-yellow-100 text-yellow-800' },
      blocked: { label: 'Blocked', className: 'bg-red-100 text-red-800' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const renderStars = (rating) => {
    if (!rating) return <span className="text-xs text-gray-400">No rating</span>;
    return (
      <div className="flex items-center space-x-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-4 w-4 ${
              i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(stats.total / pagination.limit);

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <Truck className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Suppliers / Partners</h2>
              <p className="text-sm text-[#6C757D]">Manage your supplier & partner database — suppliers, commercial partners, financiers and operational managers</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Supplier
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6C757D]" />
              <input
                type="text"
                placeholder="Search suppliers by company name, contact, or email..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination({ ...pagination, skip: 0 });
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="h-4 w-4 text-[#6C757D]" />
                <span className="text-sm text-[#6C757D]">Filters:</span>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination({ ...pagination, skip: 0 });
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="blocked">Blocked</option>
              </select>
              <input
                type="text"
                placeholder="Category"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPagination({ ...pagination, skip: 0 });
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total Suppliers / Partners</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{stats.total}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Active</p>
              <p className="text-3xl font-bold text-green-600">{stats.active}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Inactive</p>
              <p className="text-3xl font-bold text-yellow-600">{stats.inactive}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Blocked</p>
              <p className="text-3xl font-bold text-red-600">{stats.blocked}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suppliers List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading suppliers...</p>
          </CardContent>
        </Card>
      ) : suppliers.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {searchTerm || statusFilter || categoryFilter
                ? 'No suppliers found matching your filters.'
                : 'No suppliers yet. Add your first supplier!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {suppliers.map((supplier) => (
            <Card key={supplier.id} className={supplier.is_deleted ? 'opacity-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Truck className="h-5 w-5 text-[#00B4D8]" />
                      <h3 className="text-lg font-semibold text-[#2B2D42]">
                        {supplier.company_name}
                      </h3>
                      {getStatusBadge(supplier.status)}
                      {supplier.is_deleted && (
                        <Badge className="bg-gray-100 text-gray-800">Deleted</Badge>
                      )}
                      <AlsoBadge module="suppliers" recordId={supplier.id} current="supplier" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Contact Person</p>
                        <p className="text-sm text-[#2B2D42]">
                          {supplier.contact_name || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Email</p>
                        <p className="text-sm text-[#2B2D42]">{supplier.email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Phone</p>
                        <p className="text-sm text-[#2B2D42]">{supplier.phone || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Category</p>
                        <p className="text-sm text-[#2B2D42]">{supplier.category || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Payment Terms</p>
                        <p className="text-sm text-[#2B2D42]">
                          {supplier.payment_terms || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6C757D] mb-1">Rating</p>
                        {renderStars(supplier.rating)}
                      </div>
                    </div>

                    {supplier.tax_id && (
                      <div className="mt-3">
                        <p className="text-xs text-[#6C757D] mb-1">Tax ID</p>
                        <p className="text-sm text-[#2B2D42]">{supplier.tax_id}</p>
                      </div>
                    )}

                    {supplier.address && (
                      <div className="mt-3">
                        <p className="text-xs text-[#6C757D] mb-1">Address</p>
                        <p className="text-sm text-[#2B2D42]">{supplier.address}</p>
                      </div>
                    )}

                    {/* CRM audit trail — documents issued to this supplier/partner */}
                    <SupplierDocuments supplierId={supplier.id} supplierName={supplier.company_name} />
                  </div>

                  <div className="flex flex-col space-y-2 ml-4">
                    {!supplier.is_deleted ? (
                      <>
                        <Button
                          onClick={() => setPromoteSource({
                            prefixed_id: `supplier:${supplier.id}`,
                            name: supplier.company_name,
                            source: 'supplier',
                            is_partner: supplier.is_partner || (supplier.category === 'Partner'),
                          })}
                          variant="outline"
                          size="sm"
                          className="text-[#FF6B35] hover:bg-orange-50 border-orange-300"
                          title="Promote to Client / Partner / Customer / Contact / Lead"
                          data-testid={`promote-supplier-${supplier.id}`}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => navigate(`/admin/procurement/suppliers/${supplier.id}/score`)}
                          variant="outline"
                          size="sm"
                          className="border-amber-500 text-amber-600 hover:bg-amber-500 hover:text-white"
                          title="View performance scorecard"
                          data-testid={`supplier-score-btn-${supplier.id}`}
                        >
                          <Award className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(supplier)}
                          variant="outline"
                          size="sm"
                          className="border-[#00B4D8] text-[#00B4D8] hover:bg-[#00B4D8] hover:text-white"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(supplier.id)}
                          variant="outline"
                          size="sm"
                          className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => handleRestore(supplier.id)}
                        variant="outline"
                        size="sm"
                        className="border-green-500 text-green-500 hover:bg-green-500 hover:text-white"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {suppliers.length > 0 && (
        <div className="flex justify-between items-center mt-6">
          <Button
            onClick={handlePreviousPage}
            disabled={pagination.skip === 0}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-sm text-[#6C757D]">
            Page {currentPage} of {totalPages || 1}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={suppliers.length < pagination.limit}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      {/* Modals */}
      <CreateSupplierModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleSuccessCreate}
      />
      <EditSupplierModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        supplier={selectedSupplier}
        onSuccess={handleSuccessEdit}
      />
      <PromoteCounterpartyModal
        open={!!promoteSource}
        onClose={() => setPromoteSource(null)}
        source={promoteSource}
        onPromoted={() => fetchSuppliers()}
      />
    </div>
  );
};

export default Suppliers;